import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataSubscription } from './queryKeys';
import { type MutateDataSubscriptionData } from './types';

type DataSubscriptionResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

type UpdateCDataSubscriptionArguments = {
  dataSubscriptionData: MutateDataSubscriptionData;
  dataSubscriptionId: UUID;
};

/**
 * Updates a data subscription.
 *
 * @param {UUID} dataSubscriptionId - The unique identifier (UUID) of the data subscription that needs to be updated.
 * @param {MutateDataSubscriptionData} dataSubscriptionData - An object containing the data subscription details to be updated.
 * @returns {Promise<DataSubscriptionResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/update_data_subscription_data_subscription__id__put
 */
export const updateDataSubscription = async (
  dataSubscriptionId: UUID,
  dataSubscriptionData: MutateDataSubscriptionData,
) => {
  const response = await vestigasApi
    .put(
      ENDPOINT.DATA_SUBSCRIPTION.UPDATE_DATA_SUBSCRIPTION(dataSubscriptionId),
      {
        json: dataSubscriptionData,
      },
    )
    .json<DataSubscriptionResponse>();

  return response;
};

/**
 * Custom hook for updating a data subscription using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationUpdateDataSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dataSubscriptionData,
      dataSubscriptionId,
    }: UpdateCDataSubscriptionArguments) =>
      updateDataSubscription(dataSubscriptionId, dataSubscriptionData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysDataSubscription.getAllDataSubscription(),
      });
    },
  });
};
